import notie from 'notie'

/**
 * notie's alert() unconditionally calls document.activeElement.blur() before it
 * shows the toast (see notie/dist/notie.js -> `blur()` at the top of `alert`).
 *
 * That is fine for a toast fired by an explicit "Save" click, but almost every
 * inline-editing screen we have (admin Results, EventShows schedule, entries,
 * financials, ...) fires the toast from the .then() of an @change/@blur save.
 * By the time the save resolves the user has already tabbed or clicked into the
 * next field, so notie's blur causes two distinct bugs:
 *
 *   1. Focus loss — the field you just moved to goes dead and you have to click
 *      it again for every single edit.
 *   2. Phantom saves — if the field you moved to saves on @blur, notie's blur
 *      fires that handler, saving a record the user never touched.
 *
 * So we stop the blur from happening rather than undoing it afterwards. During
 * the (synchronous) blur at the top of alert() we swap the focused element's
 * blur method for a no-op, then put it straight back. No blur event is
 * dispatched, focus and caret are untouched, and no @blur handler runs.
 *
 * Undoing it instead — letting the blur fire and then re-focusing — is what an
 * earlier version of this file did, and it turns bug 2 into an infinite loop:
 * the toast blurs the field, the @blur handler saves and toasts again, that
 * toast blurs the re-focused field again, and round it goes.
 *
 * confirm()/force()/input() are intentionally left alone — those are modal and
 * are supposed to take focus away from the page.
 */

const rawAlert = notie.alert
const noop = () => {}

notie.alert = function alertWithoutStealingFocus (...args) {
  const active = document.activeElement

  // document.body (or null) is notie's own no-op case — nothing to protect.
  if (!active || active === document.body || typeof active.blur !== 'function') {
    return rawAlert.apply(this, args)
  }

  const hadOwnBlur = Object.prototype.hasOwnProperty.call(active, 'blur')
  const ownBlur = active.blur
  active.blur = noop

  try {
    return rawAlert.apply(this, args)
  } finally {
    // Restore immediately: only notie's internal call is suppressed, real user
    // blurs after this point behave normally.
    if (hadOwnBlur) active.blur = ownBlur
    else delete active.blur
  }
}

export default notie
